'use client';

import { forwardRef, type CSSProperties, type HTMLAttributes } from 'react';
import { cn } from '../../lib/cn';
import {
  SCROLLBAR_CLASS,
  SCROLLBAR_TRACK_BG_CLASS,
  SCROLLBAR_TRACK_BORDER_CLASS,
} from '../../lib/scrollbar';
import type { SScrollAreaAxis } from './scrollArea.config';

const AXIS_CLASS: Record<SScrollAreaAxis, string> = {
  vertical: 'overflow-x-hidden overflow-y-auto',
  horizontal: 'overflow-x-auto overflow-y-hidden',
  both: 'overflow-auto',
};

export interface SScrollAreaProps extends HTMLAttributes<HTMLDivElement> {
  /** 스크롤 방향. vertical=세로만, horizontal=가로만, both=양방향 */
  axis?: SScrollAreaAxis;
  /** true면 스크롤바 트랙 배경을 채운다. 콘텐츠 영역 배경은 바뀌지 않는다. */
  background?: boolean;
  /** true면 스크롤바와 콘텐츠 사이에 1px 구분선을 그린다. 컨테이너 테두리가 아니다. */
  bordered?: boolean;
  /** viewport 최대 높이 (예: '400px'). 비우면 부모 크기를 따른다. */
  maxHeight?: string;
  /** viewport 최대 너비 (예: '480px'). 비우면 부모 크기를 따른다. */
  maxWidth?: string;
}

/**
 * SScrollArea — sd-scroll-area 포팅.
 * 오버플로우 콘텐츠를 커스텀 네이티브 스크롤바로 감싸는 영역.
 * background·bordered는 컨테이너가 아니라 스크롤바에만 적용된다.
 * 크기는 소비자가 className/style 로 지정한다(호스트 오버라이드 가능).
 */
export const SScrollArea = forwardRef<HTMLDivElement, SScrollAreaProps>(function SScrollArea(
  {
    axis = 'both',
    background = false,
    bordered = false,
    maxHeight,
    maxWidth,
    className,
    style,
    children,
    ...rest
  },
  ref,
) {
  const mergedStyle: CSSProperties = { ...style };
  if (maxHeight) mergedStyle.maxHeight = maxHeight;
  if (maxWidth) mergedStyle.maxWidth = maxWidth;

  return (
    <div
      ref={ref}
      className={cn(
        AXIS_CLASS[axis],
        SCROLLBAR_CLASS,
        background && SCROLLBAR_TRACK_BG_CLASS,
        bordered && SCROLLBAR_TRACK_BORDER_CLASS,
        className,
      )}
      style={mergedStyle}
      {...rest}
    >
      {children}
    </div>
  );
});
